import { useState } from 'react';

/**
 * The pieces from the Figma component set, built to the API in
 * docs/07-component-contract.md. Hover and pressed are CSS, not props.
 */

export function Button({ variant = 'primary', size = 'l', className = '', ...props }) {
  return <button className={`btn btn-${variant} btn-${size} ${className}`} {...props} />;
}

export function Chip({ tone = 'neutral', size = 's', showDot = true, children, className = '', ...props }) {
  return (
    <span className={`chip chip-${tone} chip-${size} ${className}`} {...props}>
      {showDot && <span className="dot" />}
      {children}
    </span>
  );
}

/**
 * A password field reveals what you typed. Hiding it protects against somebody reading
 * over your shoulder, which at a front desk is a real risk, so the default stays hidden
 * and the eye is a deliberate act. Without it, a temporary password read off a card and
 * typed wrong three times locks the account, which is worse.
 */
export function Field({ label, hint, type, ...props }) {
  const [shown, setShown] = useState(false);
  const isPassword = type === 'password';
  const revealed = isPassword && shown;

  return (
    <div className="field">
      <label className="t-label-xs" htmlFor={props.id}>{label}</label>
      <div className={isPassword ? 'field-with-reveal' : undefined}>
        <input type={revealed ? 'text' : type} {...props} />
        {isPassword && (
          <button type="button" className="reveal" onClick={() => setShown(s => !s)}
            aria-label={revealed ? 'Hide the password' : 'Show the password'}
            aria-pressed={revealed} tabIndex={-1} title={revealed ? 'Hide' : 'Show'}>
            <Eye off={revealed} />
          </button>
        )}
      </div>
      {hint && <span className="t-body-s muted">{hint}</span>}
    </div>
  );
}

/** Open eye when hidden (tap to show), struck through when shown (tap to hide). */
const Eye = ({ off }) => (
  <svg viewBox="0 0 20 20" width="19" height="19" fill="none" aria-hidden="true">
    <path d="M1.5 10s3-5.5 8.5-5.5S18.5 10 18.5 10s-3 5.5-8.5 5.5S1.5 10 1.5 10Z"
      stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    <circle cx="10" cy="10" r="2.4" stroke="currentColor" strokeWidth="1.5" />
    {off && <path d="M3.5 16.5 16.5 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />}
  </svg>
);

export function Banner({ tone = 'danger', title, children }) {
  const bg = {
    danger: 'var(--color-status-danger-bg)',
    warning: 'var(--color-status-warning-bg)',
    info: 'var(--color-status-info-bg)',
    success: 'var(--color-status-success-bg)'
  }[tone];
  const fg = {
    danger: 'var(--color-status-danger)',
    warning: 'var(--color-status-warning)',
    info: 'var(--color-status-info)',
    success: 'var(--color-status-success)'
  }[tone];
  return (
    <div style={{ background: bg, color: fg, padding: 'var(--space-md) var(--space-lg)', borderRadius: 'var(--radius-md)' }}>
      {title && <div className="t-heading-s" style={{ marginBottom: 4 }}>{title}</div>}
      <div className="t-body-s">{children}</div>
    </div>
  );
}
